import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import Button from '../../components/Button';
import Callout from '../../components/Callout';
import Field from '../../components/Field';
import { WizardStatus } from '../../components/WizardShell';
import { api, ApiError, type PlanResponse } from '../../lib/api';
import { cx } from '../../lib/cx';

/**
 * The electrical and lighting section is hidden on this page for now.
 *
 * The API still computes it and the printed brief and the lead email still
 * carry it — this hides the panel on the plan page only, so turning it back on
 * is this one word. Nothing downstream was deleted.
 */
const SHOW_ELECTRICAL: boolean = false;

const n = (v: number) => v.toLocaleString('en-US');
const money = (v: number) =>
  v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${Math.round(v / 1000)}k`;

function Band({
  label,
  low,
  high,
  unit,
  format = n,
}: {
  label: string;
  low: number;
  high: number;
  unit?: string;
  format?: (v: number) => string;
}) {
  return (
    <div className="rounded-lg border border-grey-200 bg-white p-5 shadow-sm">
      <p className="t-meta text-grey-600">{label}</p>
      <p className="t-numeral mt-1 text-2xl text-ink">
        {/* A footprint on one floor has no range; showing "16,000 - 16,000"
            reads like a bug rather than like precision. */}
        {low === high ? (
          format(low)
        ) : (
          <>
            {format(low)}
            <span className="text-grey-500"> – </span>
            {format(high)}
          </>
        )}
        {unit && <span className="t-meta ml-1 font-normal text-grey-600">{unit}</span>}
      </p>
    </div>
  );
}

function Section({
  title,
  intro,
  children,
  accent = false,
}: {
  title: string;
  intro?: string;
  children: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <section className={cx('border-t border-grey-200 pt-10', accent && 'border-t-[3px] border-t-green-600')}>
      <h2 className="t-h2 text-ink">{title}</h2>
      {intro && <p className="t-body measure mt-2 text-grey-600">{intro}</p>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

const Notes = ({ notes }: { notes: string[] }) => (
  <ul className="mt-5 flex flex-col gap-2">
    {notes.map((t) => (
      <li key={t} className="t-meta measure text-grey-600">
        {t}
      </li>
    ))}
  </ul>
);

export default function Plan() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PlanResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api
      .getPlan(id)
      .then((d) => !cancelled && setData(d))
      .catch((e) =>
        !cancelled &&
        setError(e instanceof ApiError ? e.message : 'We could not work these out just now.'),
      );
    return () => {
      cancelled = true;
    };
  }, [id]);

  const requestQuote = async () => {
    if (!id) return;
    setSending(true);
    try {
      await api.requestQuote(id, note);
      setSent(true);
    } catch {
      setError('That did not send. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  if (error && !data) return <WizardStatus message={error} />;
  if (!data) return <WizardStatus message="Working these out…" />;

  const { site, occupancy, budget, electrical, phasing } = data;

  return (
    <AppShell>
      <p className="t-eyebrow text-green-600">{data.verticalLabel}</p>
      <h1 className="t-h1 mt-3 text-ink">What this building needs</h1>
      <p className="t-body-lg measure mt-4 text-grey-600">
        {n(data.sizeSf)} sf over {data.stories} {data.stories === 1 ? 'story' : 'stories'}
        {data.systemLabel ? `, built as ${data.systemLabel.toLowerCase()}` : ''}. Everything
        below is a range, and every one of them gets settled by somebody licensed before it
        gets built.
      </p>

      <div className="mt-12 flex flex-col gap-12">
        {site && (
          <Section
            title="Site and parking"
            intro={`Parking is figured on ${site.parkingBasis}.`}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Band label="Building footprint" low={site.footprint.low} high={site.footprint.high} unit="sf" />
              <Band label="Parking spaces" low={site.stalls.low} high={site.stalls.high} />
              <Band label="Paved for parking" low={site.parkingArea.low} high={site.parkingArea.high} unit="sf" />
              <Band
                label="Land needed"
                low={site.acres.low}
                high={site.acres.high}
                unit="acres"
                format={(v) => v.toFixed(1)}
              />
            </div>
            {site.fitWarning && (
              <div className="mt-6">
                <Callout tone="caution" title="Your site may be tight">
                  {site.fitWarning}
                </Callout>
              </div>
            )}
            <Notes notes={site.notes} />
          </Section>
        )}

        {SHOW_ELECTRICAL && electrical && (
          <Section
            title="Electrical and lighting"
            intro="Planning figures, so nothing about the power service comes as a surprise later."
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Band label="Connected load" low={electrical.connectedLoadKw.low} high={electrical.connectedLoadKw.high} unit="kW" />
              <Band label="Demand load" low={electrical.demandLoadKw.low} high={electrical.demandLoadKw.high} unit="kW" />
              <div className="rounded-lg border border-green-600 bg-green-050 p-5">
                <p className="t-meta text-green-900">Service size</p>
                <p className="t-numeral mt-1 text-2xl text-green-900">{electrical.serviceAmps}A</p>
                <p className="t-meta mt-1 text-green-900">{electrical.serviceDescription}</p>
              </div>
              <Band
                label="Lighting power budget"
                low={electrical.lightingPowerBudgetKw.low}
                high={electrical.lightingPowerBudgetKw.high}
                unit="kW"
                format={(v) => v.toFixed(1)}
              />
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Band label="Interior fixtures" low={electrical.interiorFixtures.low} high={electrical.interiorFixtures.high} />
              <Band label="Exterior and site fixtures" low={electrical.exteriorFixtures.low} high={electrical.exteriorFixtures.high} />
            </div>

            {(electrical.evReady || electrical.solarReady || electrical.generator) && (
              <div className="mt-6 flex flex-col gap-4">
                {electrical.evReady && <Callout title="EV charging">{electrical.evReady}</Callout>}
                {electrical.solarReady && <Callout title="Solar">{electrical.solarReady}</Callout>}
                {electrical.generator && <Callout title="Standby power">{electrical.generator}</Callout>}
              </div>
            )}

            <Notes notes={electrical.notes} />
          </Section>
        )}

        {budget && (
          <Section title="Budget, very roughly">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Band label="Cost per square foot" low={budget.perSf.low} high={budget.perSf.high} format={(v) => `$${v}`} />
              <Band label="Hard cost" low={budget.hard.low} high={budget.hard.high} format={money} />
              <Band label="Soft cost" low={budget.soft.low} high={budget.soft.high} format={money} />
              <div className="rounded-lg border border-ink bg-ink p-5">
                <p className="t-meta text-grey-400">Total</p>
                <p className="t-numeral mt-1 text-2xl text-white">
                  {money(budget.total.low)}
                  <span className="text-grey-400"> – </span>
                  {money(budget.total.high)}
                </p>
              </div>
            </div>
            {budget.systemAdjustment && (
              <p className="t-meta measure mt-4 text-grey-600">{budget.systemAdjustment}</p>
            )}
            <Notes notes={budget.notes} />
          </Section>
        )}

        {occupancy && (
          <Section
            title="Code, at a glance"
            intro={`Likely occupancy group ${occupancy.occupancyGroup}, figured at ${occupancy.loadFactorNote}.`}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Band label="Occupant load" low={occupancy.occupantLoad.low} high={occupancy.occupantLoad.high} />
              <div className="rounded-lg border border-grey-200 bg-white p-5 shadow-sm">
                <p className="t-meta text-grey-600">Exits required</p>
                <p className="t-numeral mt-1 text-2xl text-ink">{occupancy.minimumExits}</p>
              </div>
              <Band label="Water closets" low={occupancy.waterClosets.low} high={occupancy.waterClosets.high} />
              <Band label="Lavatories" low={occupancy.lavatories.low} high={occupancy.lavatories.high} />
            </div>
            <Notes notes={occupancy.notes} />
          </Section>
        )}

        {phasing && (
          <Section title="If you build it in phases">
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-grey-200 bg-white p-5 shadow-sm">
                <p className="t-h3 text-ink">Build now</p>
                <ul className="mt-3 flex flex-col gap-2">
                  {phasing.phaseOne.map((x) => (
                    <li key={x} className="t-meta text-grey-600">{x}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-grey-200 bg-white p-5 shadow-sm">
                <p className="t-h3 text-ink">Add later</p>
                <ul className="mt-3 flex flex-col gap-2">
                  {phasing.later.map((x) => (
                    <li key={x} className="t-meta text-grey-600">{x}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-green-600 bg-green-050 p-5">
                <p className="t-h3 text-green-900">Stub in now anyway</p>
                <p className="t-meta mt-1 text-green-900">Cheap during construction, expensive afterwards.</p>
                <ul className="mt-3 flex flex-col gap-2">
                  {phasing.stubNow.map((x) => (
                    <li key={x} className="t-meta text-green-900">{x}</li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="t-meta measure mt-5 text-grey-600">{phasing.note}</p>
          </Section>
        )}

        {/* The lead capture. It used to live inside the electrical section; it
            is a section of its own now so that hiding that panel does not take
            the only route back to the team with it. */}
        <div className="rounded-lg border border-grey-200 bg-grey-050 p-6 sm:p-8">
          {sent ? (
            <Callout tone="confirm" title="Sent">
              The team has your project and the figures above, and will come back to
              you directly.
            </Callout>
          ) : (
            <>
              <h3 className="t-h3 text-ink">Send this to the team</h3>
              <p className="t-body measure mt-2 text-grey-600">
                Send the project over and the team will look closer at the figures
                above and come back to you.
              </p>
              <div className="mt-5 max-w-xl">
                <Field
                  id="quote-note"
                  label="Anything the team should know"
                  hint="Timing, budget, who else is involved — whatever is useful."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <div className="mt-5">
                <Button onClick={() => void requestQuote()} disabled={sending}>
                  {sending ? 'Sending…' : 'Send this to the team'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-12 flex flex-col-reverse gap-3 border-t border-grey-200 pt-8 sm:flex-row sm:justify-between">
        <Button variant="secondary" block onClick={() => navigate(`/project/${id}/results`)}>
          Back to my style
        </Button>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button variant="secondary" block onClick={() => navigate(`/project/${id}/brief`)}>
            Print the brief
          </Button>
          <Button block onClick={() => navigate(`/project/${id}/concepts`)}>
            See my concepts
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
